import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Award, Users } from 'lucide-react';

const Home = () => {
  useEffect(() => {
    // Add fade-in animation on mount
    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach((el, index) => {
      setTimeout(() => {
        el.style.animationDelay = `${index * 0.2}s`;
      }, 100);
    });
  }, []);

  return (
    <div style={{ backgroundColor: 'var(--color-white)' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '90vh',
        minHeight: '600px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_creative-studio-292/artifacts/a11v93zp_HeroPciture.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.7)'
        }} />
        
        {/* Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%)'
        }} />

        {/* Hero Content */}
        <div className="container-artworld" style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
          <h1 className="hero-title fade-in-up" style={{ 
            color: 'var(--color-white)', 
            marginBottom: 'var(--spacing-md)',
            animationDelay: '0.2s'
          }}>
            Art That Heals, Inspires, and Builds Communities
          </h1>
          <p className="body-text fade-in-up" style={{ 
            color: 'var(--color-gray-200)', 
            maxWidth: '600px', 
            margin: '0 auto var(--spacing-xl)',
            fontSize: '1.25rem',
            animationDelay: '0.4s'
          }}>
            Bringing people together through creativity, education, and community impact
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/gallery" style={{ textDecoration: 'none' }}>
              <button className="btn-primary fade-in-up" style={{ animationDelay: '0.6s' }}>
                View Gallery <ArrowRight size={16} style={{ marginLeft: '8px', display: 'inline' }} />
              </button>
            </Link>
            <Link to="/contact" style={{ textDecoration: 'none' }}>
              <button className="btn-inverse fade-in-up" style={{ 
                animationDelay: '0.8s',
                color: 'var(--color-white)',
                borderColor: 'var(--color-white)'
              }}>
                Get in Touch
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing-large" style={{ backgroundColor: 'var(--color-gray-100)' }}>
        <div className="container-artworld">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            What I Offer
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--spacing-xl)'
          }}>
            {[
              {
                icon: <Palette size={40} />,
                title: 'Art Workshops',
                items: [
                  'Community art workshops',
                  'Upcycling art workshops',
                  'Mixed media workshops',
                  'Cultural art workshops'
                ]
              },
              {
                icon: <Users size={40} />,
                title: 'Paint Nights Event',
                items: [
                  'Glow in the dark paint night',
                  'Corporate events',
                  'Birthday paint parties',
                  'Beginner-friendly sessions'
                ]
              },
              {
                icon: <Award size={40} />,
                title: 'Art Services',
                items: [
                  'Face Painting',
                  'Henna Tattoos',
                  'Kids event activities'
                ]
              },
              {
                icon: <Palette size={40} />,
                title: 'Event Decoration',
                items: [
                  'Handmade decorations',
                  'Cultural decorations',
                  'Art-based event styling',
                  'Custom decor'
                ]
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="fade-in-up"
                style={{
                  backgroundColor: 'var(--color-white)',
                  padding: 'var(--spacing-xl)',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  transition: 'transform var(--transition-base), box-shadow var(--transition-base)',
                  animationDelay: `${index * 0.2}s`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                }}
              >
                <div style={{ color: 'var(--color-primary)', marginBottom: 'var(--spacing-md)' }}>
                  {feature.icon}
                </div>
                <h3 className="artist-name" style={{ marginBottom: 'var(--spacing-sm)', fontSize: '1.5rem' }}>
                  {feature.title}
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                  {feature.items.map((item, idx) => (
                    <li key={idx} className="body-text" style={{ marginBottom: 'var(--spacing-xs)', fontSize: '0.95rem' }}>
                      • {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Me Section - Simplified */}
      <section className="section-spacing-large" style={{
        position: 'relative',
        minHeight: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_creative-studio-292/artifacts/mnsww1ll_MelodicTrio.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden'
      }}>
        {/* Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }} />
        
        {/* Content */}
        <div className="container-artworld" style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
          <div className="type-indicator" style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-gray-300)' }}>
            About the Artist
          </div>
          <p className="body-text" style={{ 
            color: 'var(--color-white)', 
            fontSize: '1.25rem',
            maxWidth: '900px',
            margin: '0 auto var(--spacing-xl)',
            lineHeight: '1.8'
          }}>
            Saloni Sharma is a Calgary based multidisciplinary artist, interior designer and art educator. Her work explores sustainability, cultural heritage, and community connection through mixed media and functional craft using recycled and natural materials. Saloni combines traditional folk-art influences with contemporary experimentation to create work that reflects transformation, memory, and everyday life. Alongside her studio practice, she actively engages with the community through workshops, artist projects, and collaborations with local organizations.
          </p>
          
          <Link to="/about" style={{ textDecoration: 'none' }}>
            <button className="btn-primary" style={{
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-primary)',
              borderColor: 'var(--color-white)'
            }}>
              Learn More About My Journey
            </button>
          </Link>
        </div>
      </section>

      {/* Featured Work Preview */}
      <section className="section-spacing-large">
        <div className="container-artworld">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
            <h2 className="section-title" style={{ marginBottom: 'var(--spacing-md)' }}>
              Featured Works
            </h2>
            <p className="body-text" style={{ maxWidth: '600px', margin: '0 auto' }}>
              A glimpse into my artistic journey, showcasing diverse styles and mediums
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-xl)'
          }}>
            {[
              { url: 'https://api.artwithsaloni.com/media/gallery/Smiriti_Mool_16x18.jpg', title: 'Smriti Mool (Roots of Memory)' },
              { url: 'https://api.artwithsaloni.com/media/gallery/Garden_of_life_16X20_x3OplKS.jpg', title: 'Garden of Life' },
              { url: 'https://api.artwithsaloni.com/media/gallery/Pravah_Flow_16x18_7avAjo3.jpg', title: 'Pravah (Flow)' }
            ].map((work, index) => (
              <div
                key={index}
                className="image-overlay-container"
                style={{
                  aspectRatio: '1 / 1',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={work.url}
                  alt={work.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform var(--transition-slow)'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                />
                <div className="image-overlay" />
                <div className="image-overlay-content">
                  <h3 className="artist-name" style={{ color: 'var(--color-white)', fontSize: '1.25rem' }}>
                    {work.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <Link to="/gallery" style={{ textDecoration: 'none' }}>
              <button className="btn-primary">
                View Full Gallery
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-spacing-large" style={{
        backgroundColor: 'var(--color-gray-900)',
        color: 'var(--color-white)'
      }}>
        <div className="container-artworld" style={{ textAlign: 'center' }}>
          <h2 className="section-title" style={{ color: 'var(--color-white)', marginBottom: 'var(--spacing-md)' }}>
            Let's Create Together
          </h2>
          <p className="body-text" style={{ 
            color: 'var(--color-gray-300)', 
            maxWidth: '600px', 
            margin: '0 auto var(--spacing-xl)',
            fontSize: '1.125rem'
          }}>
            Whether you're interested in purchasing artwork, commissioning a piece, or joining a community workshop
          </p>
          <Link to="/contact" style={{ textDecoration: 'none' }}>
            <button className="btn-primary" style={{
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-primary)',
              borderColor: 'var(--color-white)'
            }}>
              Contact Me
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
